import "./Dropdown.scss";
import { useRef } from "react";
import { DropdownButton } from "/App.Components";
import { DropdownItemProps, DropdownProps } from "/App.GeneratedTypes";
import { DesignTheme } from "../../../App.GeneratedEnums";
import { createSpreadableBem } from "../../../Helpers/createBem";
import useClickOutsideElement from "../../../Helpers/Hooks/useClickOutsideElement";
import { DropdownState } from "../DropdownButton/DropdownButton";

export interface Props extends DropdownProps {
  selected?: DropdownItemProps;
  setSelected?: (value: DropdownItemProps) => void;
  ariaLabel?: string;
  arrowDownHandlerFromParent?: (currentlyFocused: HTMLButtonElement) => void;
  arrowUpHandlerFromParent?: (currentlyFocused: HTMLButtonElement) => void;
  escHandlerFromParent?: () => void;
  tabHandlerFromParent?: () => void;
  onKeyboardSelectFromParent?: () => void;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  lastButtonRoundBottomCorners?: boolean;
  roundTopCorners?: boolean;
  roundBottomCorners?: boolean;
  parentHandlesCloseOnClickOutside?: boolean;
  dropdownState?: DropdownState;
  noTabStopOnMainButton?: boolean;
  hasSelected?: boolean;
  theme?: DesignTheme;
}

const Dropdown = ({
  label,
  items,
  setIsOpen,
  isOpen,
  selected,
  setSelected,
  ariaLabel,
  lastButtonRoundBottomCorners = true,
  roundTopCorners = true,
  roundBottomCorners = true,
  defaultText,
  parentHandlesCloseOnClickOutside,
  dropdownState = DropdownState.standard,
  arrowDownHandlerFromParent,
  arrowUpHandlerFromParent,
  escHandlerFromParent,
  tabHandlerFromParent,
  onKeyboardSelectFromParent,
  noTabStopOnMainButton,
  hasSelected,
  theme,
}: Props) => {
  const refRoot = useRef<HTMLDivElement>(null);
  const refMainButton = useRef<HTMLDivElement>(null);
  const refDropdownContainer = useRef<HTMLUListElement>(null);

  useClickOutsideElement(() => {
    !parentHandlesCloseOnClickOutside && !!setIsOpen && setIsOpen(false);
  }, [refMainButton, refDropdownContainer]);

  const focusNextItem = (
    currentlyFocused: HTMLButtonElement,
    next: boolean
  ) => {
    const root = refRoot.current;
    if (!root) return;
    const buttons = Array.from(root.querySelectorAll("button"));
    const currentIndex = buttons.indexOf(currentlyFocused);
    let newIndex = currentIndex + (next ? 1 : -1);
    if (newIndex === -1) newIndex = buttons.length - 1;
    if (newIndex === buttons.length) newIndex = 0;
    buttons[newIndex].focus();
  };

  const arrowDownHandler = (currentlyFocused: HTMLButtonElement) => {
    if (!isOpen) setIsOpen && setIsOpen(true);
    else focusNextItem(currentlyFocused, true);
  };

  const arrowUpHandler = (currentlyFocused: HTMLButtonElement) => {
    if (!isOpen) setIsOpen && setIsOpen(true);
    else focusNextItem(currentlyFocused, false);
  };

  const escHandler = () => {
    if (isOpen) {
      setIsOpen && setIsOpen(false);
      Array.from(
        refMainButton.current?.querySelectorAll("button") ?? []
      )[0]?.focus();
    } else {
      escHandlerFromParent && escHandlerFromParent();
    }
  };

  const tabHandler = () => isOpen && setIsOpen && setIsOpen(false);
  const bem = createSpreadableBem("dropdown-norad");

  return (
    <div {...bem("")} ref={refRoot}>
      {label && <p {...bem("__label")}>{label}</p>}
      <div ref={refMainButton}>
        <DropdownButton
          onClick={() => {
            !!setIsOpen && setIsOpen(!isOpen);
          }}
          text={defaultText ?? ""}
          ariaLabel={ariaLabel ?? `${defaultText ?? ""}, se alle valg`}
          onArrowDown={
            isOpen
              ? arrowDownHandler
              : (arrowDownHandlerFromParent ?? arrowDownHandler)
          }
          onArrowUp={
            isOpen
              ? arrowUpHandler
              : (arrowUpHandlerFromParent ?? arrowUpHandler)
          }
          onEsc={isOpen ? escHandler : (escHandlerFromParent ?? escHandler)}
          roundTopCorners={roundTopCorners}
          roundBottomCorners={roundBottomCorners && !isOpen}
          dropdownState={
            dropdownState === DropdownState.standard
              ? isOpen
                ? DropdownState.dropdownOpen
                : DropdownState.dropdownClosed
              : isOpen
                ? DropdownState.dropdownSublevel1Open
                : DropdownState.dropdownSublevel1Closed
          }
          noTabStop={noTabStopOnMainButton}
          onTab={tabHandlerFromParent ?? tabHandler}
          hasSelected={hasSelected}
          theme={theme}
        />
      </div>
      {isOpen && (items?.length ?? 0) > 0 && (
        <ul ref={refDropdownContainer} {...bem("__items")}>
          {items.map((item, idx) => (
            <DropdownButton
              key={idx}
              dropdownState={dropdownState}
              ariaLabel={item.name ?? ""}
              onClick={() => {
                !!setSelected && setSelected(item);
                !!setIsOpen && setIsOpen(false);
              }}
              onKeyboardSelect={() => {
                !!setSelected && setSelected(item);
                !!onKeyboardSelectFromParent && onKeyboardSelectFromParent();
                !onKeyboardSelectFromParent && !!setIsOpen && setIsOpen(false);
              }}
              onArrowDown={arrowDownHandler}
              onArrowUp={arrowUpHandler}
              onEsc={escHandler}
              onTab={tabHandlerFromParent ?? tabHandler}
              selected={!!item && item?.value === selected?.value}
              text={item.name ?? ""}
              roundBottomCorners={
                lastButtonRoundBottomCorners && idx === items.length - 1
              }
              noTabStop
              theme={theme}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
export default Dropdown;
